import api from "../utils/api";
import { isGuid } from "../utils/string";
import { LOGOUT } from "./types";
import { pack } from "./alert";

const fail = (dispatch, error, message) => {
  if (error?.response?.status === 401) {
    dispatch({ type: LOGOUT });
    return;
  }

  dispatch(pack([error?.response?.data?.error?.message || error?.response?.data?.message || message], "error"));
};

export const events = () => async (dispatch) => {
  try {
    const res = await api.get("/events");
    return res.data || { items: [], shapes: [] };
  } catch (error) {
    fail(dispatch, error, "workflow events failed to load");
    return { items: [], shapes: [] };
  }
};

export const workflows = () => async (dispatch) => {
  try {
    const res = await api.get("/events/workflows");
    return res.data?.items || [];
  } catch (error) {
    fail(dispatch, error, "workflows failed to load");
    return [];
  }
};

export const saveWorkflow = ({ uuid, name, caption, shape = "lead", steps = [], enabled = true, meta = {} }) => async (dispatch) => {
  try {
    const payload = { name, caption, shape, steps, enabled, meta };
    const res = isGuid(uuid)
      ? await api.put(`/events/workflows/${uuid}`, payload)
      : await api.post("/events/workflows", payload);

    if (res.data?.success === false) dispatch(pack(res.data.errors || ["workflow could not be saved"], "error"));
    else dispatch(pack(["workflow saved"], "success"));
    return res.data;
  } catch (error) {
    fail(dispatch, error, "workflow could not be saved");
    return { success: false };
  }
};

export const deleteWorkflow = (uuid) => async (dispatch) => {
  try {
    const res = isGuid(uuid) ? await api.delete(`/events/workflows/${uuid}`) : { data: { success: false } };
    if (res.data?.success) dispatch(pack(["workflow deleted"], "success"));
    return res.data;
  } catch (error) {
    fail(dispatch, error, "workflow could not be deleted");
    return { success: false };
  }
};

export const eventWorkflows = (eventKey) => async (dispatch) => {
  try {
    const res = await api.get(`/events/${encodeURIComponent(eventKey)}/workflows`);
    return res.data || { items: [] };
  } catch (error) {
    fail(dispatch, error, "event workflow assignments failed to load");
    return { items: [] };
  }
};

export const assignWorkflow = ({ eventKey, workflowUuid, enabled = true, conditions = {}, meta = {} }) => async (dispatch) => {
  try {
    const res = await api.post(`/events/${encodeURIComponent(eventKey)}/workflows`, {
      workflowUuid,
      enabled,
      conditions,
      meta,
    });
    if (res.data?.success === false) dispatch(pack(res.data.errors || ["workflow assignment failed"], "error"));
    return res.data;
  } catch (error) {
    fail(dispatch, error, "workflow assignment failed");
    return { success: false };
  }
};

export const updateWorkflowAssignment = ({ eventKey, workflowUuid, enabled = true, conditions = {}, meta = {} }) => async (dispatch) => {
  try {
    const res = await api.put(`/events/${encodeURIComponent(eventKey)}/workflows/${workflowUuid}`, {
      enabled,
      conditions,
      meta,
    });
    if (res.data?.success === false) dispatch(pack(res.data.errors || ["workflow assignment failed"], "error"));
    return res.data;
  } catch (error) {
    fail(dispatch, error, "workflow assignment failed");
    return { success: false };
  }
};

export const deleteWorkflowAssignment = ({ eventKey, workflowUuid }) => async (dispatch) => {
  try {
    const res = await api.delete(`/events/${encodeURIComponent(eventKey)}/workflows/${workflowUuid}`);
    return res.data;
  } catch (error) {
    fail(dispatch, error, "workflow assignment could not be removed");
    return { success: false };
  }
};

export const campaigns = () => async (dispatch) => {
  try {
    const res = await api.get("/leads/campaigns");
    return res.data?.items || [];
  } catch (error) {
    fail(dispatch, error, "campaigns failed to load");
    return [];
  }
};

export const saveCampaign = (payload) => async (dispatch) => {
  try {
    const res = await api.post("/leads/campaigns", payload);
    if (res.data?.success === false) dispatch(pack(res.data.errors || ["campaign could not be saved"], "error"));
    else dispatch(pack(["campaign saved"], "success"));
    return res.data;
  } catch (error) {
    fail(dispatch, error, "campaign could not be saved");
    return { success: false };
  }
};

export const deleteCampaign = (uuid) => async (dispatch) => {
  try {
    const res = isGuid(uuid) ? await api.delete(`/leads/campaigns/i/${uuid}`) : { data: { success: false } };
    if (res.data?.success) dispatch(pack(["campaign deleted"], "success"));
    return res.data;
  } catch (error) {
    fail(dispatch, error, "campaign could not be deleted");
    return { success: false };
  }
};

export const enforceCampaigns = ({ limit = 50, dryRun = true, detail = true, campaignUuid = null } = {}) => async (dispatch) => {
  try {
    const res = await api.post("/leads/campaigns/enforce", { limit, dryRun, detail, campaignUuid });
    return res.data;
  } catch (error) {
    fail(dispatch, error, "campaign enforcement failed");
    return { success: false };
  }
};

export const enforceLeadWorkflow = ({ limit = 100, dryRun = true, detail = true } = {}) => async (dispatch) => {
  try {
    const res = await api.post("/leads/workflow/enforce", { limit, dryRun, detail });
    return res.data;
  } catch (error) {
    fail(dispatch, error, "lead workflow enforcement failed");
    return { success: false };
  }
};
